import { create, v, w } from '@dojo/framework/core/vdom';
import { icache } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { IconButton } from './iconButton';
import { HeadingSelect } from './headingSelect';
import { Link } from './link';

import css from '../css/editor.m.css';
import { subscribe } from '@holmescorp/pub-sub';

export interface ToolbarProperties {}

const factory = create({
	icache,
	theme
}).properties<ToolbarProperties>();

const defaults: { [key: string]: string } = {
	bold: 'bold',
	italic: 'italic',
	underline: 'underline',
	'list-ol': 'insertorderedList',
	'list-ul': 'insertunorderedList',
	indent: 'indent',
	outdent: 'outdent',
	undo: 'undo',
	redo: 'redo'
};

export const EditorToolbar = factory(({ middleware: { icache, theme } }) => {
	const range: Range = icache.getOrSet('range', new Range());

	const getInitialValue = (range: Range): string => {
		const selection = document.getSelection();
		selection?.removeAllRanges();
		selection?.addRange(range);
		const container = selection?.anchorNode;
		const initialValue = container?.nodeType === 3 ? container?.parentNode?.nodeName.toLowerCase() : 'div';
		return initialValue as string;
	};

	subscribe('current:selection', (topic, range) => {
		icache.set('range', range);
	});

	const buttons = Object.keys(defaults).map((iconName) => {
		return icache.getOrSet(
			iconName,
			w(IconButton, {
				iconName,
				onClick: () => {
					const command = defaults[iconName];
					document.execCommand(command, false, undefined);
					document.getElementById('editor')?.focus();
				}
			})
		);
	});

	const { toolbar } = theme.classes(css);

	const disabled = range.collapsed;
	const title = disabled ? 'Please make a Selection' : '';

	return v('div', { classes: [toolbar] }, [
		w(HeadingSelect, {
			value: getInitialValue(range),
			disabled,
			title,
			onValue: (value: string) => {
				document.execCommand('formatBlock', false, value);
				document.getElementById('editor')?.focus();
			}
		}),
		...buttons,
		w(Link, {
			disabled,
			title,
			onLink: (url: string, target: string) => {
				// Delete the selection
				const selection = document.getSelection();
				if (selection && range instanceof Range) {
					selection.removeAllRanges();
					selection.addRange(range);
				}

				// Create the anchor
				const a = document.createElement('a');
				a.href = url;
				a.target = target;
				a.title = a.outerHTML;
				a.text = selection ? selection.toString() : url;

				document.execCommand('delete', false);
				document.execCommand('insertHTML', false, a.outerHTML);
				document.getElementById('editor')?.focus();
			}
		}),
		w(IconButton, {
			iconName: 'unlink',
			disabled,
			title,
			onClick: () => {
				document.execCommand('unlink', false, undefined);
				document.getElementById('editor')?.focus();
			}
		})
	]);
});

export default EditorToolbar;
